/**
 * Shared order conversion utilities
 * Single source of truth for order data transformations
 */
import marketdata from "../data/Services/marketdata";

/**
 * Converts router order data to standardized order object
 * @param {Object} routerOrder - Raw order data from router
 * @param {string} symbol - Order symbol
 * @returns {Object} Standardized order object
 */
export const convertRouterOrderToOrderObject = (routerOrder, symbol) => {
  // Determine order type based on price fields
  let orderType = "MARKET";
  if (routerOrder.stop && routerOrder.stop > 0) {
    orderType = "STOPLIMIT";
  } else if (routerOrder.limit && routerOrder.limit > 0) {
    orderType = "LIMIT";
  }

  // Get the order price - STOPLIMIT uses stop trigger, others use limit first
  const price =
    orderType === "STOPLIMIT"
      ? routerOrder.stop || routerOrder.limit || routerOrder.price || 0
      : routerOrder.limit || routerOrder.stop || routerOrder.price || 0;

  // Determine order side
  const side = (routerOrder.side || routerOrder.action || "buy").toLowerCase();

  // Determine order state - standardized to always use 'open' for active orders
  let state = "open"; // Default for router orders
  const status =
    routerOrder.status || routerOrder.state || routerOrder.OrdStatus || "";

  switch (status.toLowerCase()) {
    case "filled":
      state = "filled";
      break;
    case "cancelled":
      state = "cancelled";
      break;
    case "rejected":
      state = "rejected";
      break;
    case "partial":
      state = "partial";
      break;
    case "active":
    case "open":
    case "pending":
    default:
      state = "open"; // Standardized: always 'open' for active/pending orders
  }

  // Map nested OTO legs (flat, not recursively re-normalizing to avoid deep nesting issues)
  let oto = [];
  if (Array.isArray(routerOrder.oto)) {
    oto = routerOrder.oto.map((leg) => {
      const legSide = (
        leg.side ||
        leg.action ||
        (side === "buy" ? "sell" : "buy")
      ).toLowerCase();
      const orderType = leg.stop && leg.stop > 0 ? "STOPLIMIT" : "LIMIT";
      // Determine OTO type based on order type for React component compatibility
      const otoType = orderType === "STOPLIMIT" ? "stop" : "profit";

      return {
        id:
          leg.id ||
          leg.legId ||
          `${routerOrder.id || symbol}_OTO_${Date.now()}`,
        symbol: leg.symbol || symbol,
        side: legSide,
        quantity: leg.quantity || leg.qty || leg.qtt || 0,
        price:
          orderType === "STOPLIMIT"
            ? leg.stop || leg.limit || leg.price || 0
            : leg.limit || leg.stop || leg.price || 0,
        limit: leg.limit,
        stop: leg.stop,
        orderType: orderType,
        type: otoType, // Add required type field for React components
        state: (leg.state || leg.status || "open").toLowerCase(),
        isRouterOrder: true,
        originalRouterOrder: leg,
      };
    });
  }

  return {
    id: routerOrder.id || `router_${symbol}_${Date.now()}`,
    symbol: symbol,
    side: side,
    quantity: routerOrder.quantity || routerOrder.qty || routerOrder.qtt || 0,
    price: price,
    orderType: orderType,
    state: state,
    isRouterOrder: true,
    originalRouterOrder: routerOrder,
    oto: oto.length ? oto : undefined,
    oco: routerOrder.oco, // Preserve OCO relationship field
  };
};

/**
 * Converts standardized order object to router order format for submission
 * @param {Object} order - Standardized order object
 * @returns {Object} Router-compatible order data
 */
export const convertOrderObjectToRouterOrder = (order) => {
  // Always translate symbol to ensure actual contract is used (WINFUL → WING26)
  // Single gated order-resolution authority — translates FUT/FUL AND enforces the
  // 08:55 freshness gate (issue #351). This is the submit chokepoint every new
  // order funnels through, so an un-gated alias can never reach the broker.
  const translatedSymbol = marketdata.resolveSymbolForTrading(order.symbol);

  const routerOrder = {
    id: order.id,
    symbol: translatedSymbol,
    action: order.side, // Router uses 'action' instead of 'side'
    quantity: order.quantity,
    tif: "DAY",
  };

  // Add price fields based on order type
  if (order.orderType === "LIMIT" && order.price > 0) {
    routerOrder.limit = order.price;
  } else if (order.orderType === "STOPLIMIT" && order.price > 0) {
    routerOrder.stop = order.price;
  }
  // For MARKET orders, no price fields are added

  // Map attached OTO legs (profit/stop) if present
  if (Array.isArray(order.oto) && order.oto.length) {
    routerOrder.oto = order.oto.map((leg) => {
      const legObj = {
        id: leg.id,
        action: leg.side || (order.side === "buy" ? "sell" : "buy"),
        quantity: leg.quantity,
        symbol: marketdata.resolveSymbolForTrading(leg.symbol || order.symbol),
        tif: "DAY",
      };
      if (leg.orderType === "LIMIT" && leg.price > 0) {
        legObj.limit = leg.price;
      } else if (leg.orderType === "STOPLIMIT" && leg.price > 0) {
        legObj.stop = leg.price;
      }
      return legObj;
    });
  }

  return routerOrder;
};

/**
 * Gets human-readable order type display
 * @param {Object} order - Order object
 * @returns {string} Display string like "COMPRA LMT"
 */
export const getOrderTypeDisplay = (order) => {
  const side = (order.side || order.action || "buy").toLowerCase();
  const sideText = side === "buy" ? "COMPRA" : "VENDA";

  let typeText = "MKT";
  if (order.orderType === "LIMIT") {
    typeText = "LMT";
  } else if (order.orderType === "STOPLIMIT") {
    typeText = "STP";
  }

  return `${sideText} ${typeText}`;
};

/**
 * Gets human-readable order status display
 * @param {Object} order - Order object
 * @returns {string} Display string like "ATIVA", "EXEC.", etc.
 */
export const getOrderStatusDisplay = (order) => {
  const state = order.state || "unknown";

  switch (state) {
    case "local":
      return "LOCAL";
    case "pending":
      return "PROC.";
    case "open":
      return "ATIVA"; // Standardized: always shows 'ATIVA' for open orders
    case "partial":
      return "PARCIAL";
    case "filled":
      return "EXEC.";
    case "cancelled":
      return "CANC.";
    case "rejected":
      return "REJ.";
    default:
      return state.toUpperCase().substring(0, 6);
  }
};

// Derive OTO leg kind on demand without persisting a 'type' property
export const getOtoLegKind = (leg) => {
  if (!leg) return "unknown";
  const hasStop =
    leg.orderType === "STOPLIMIT" ||
    (leg.originalRouterOrder && leg.originalRouterOrder.stop);
  const hasLimit =
    leg.orderType === "LIMIT" ||
    (leg.originalRouterOrder && leg.originalRouterOrder.limit);
  if (hasStop && !hasLimit) return "stop";
  return "profit";
};

/**
 * Gets Portuguese display label for OTO leg types
 * @param {string} otoType - OTO type ('stop' or 'profit')
 * @returns {string} Display label in Portuguese
 */
export const getOtoTypeLabel = (otoType) => {
  switch (otoType) {
    case "stop":
      return "PERDA";
    case "profit":
      return "GANHO";
    default:
      return otoType.toUpperCase();
  }
};

/**
 * Compares original and new order values to detect actual changes
 * Prevents unnecessary router submissions when values haven't changed
 *
 * After the Phase 1 refactor, originalRouterOrder is always kept current by
 * updateOrderFromReport, so we can compare directly against order.price / order.quantity.
 *
 * @param {Object} originalOrder - Original order object (from orderStore, always up-to-date)
 * @param {Object} newValues - New values to compare (price, quantity, oto)
 * @returns {boolean} true if there are actual changes, false otherwise
 */
export const hasOrderChanges = (originalOrder, newValues) => {
  if (!originalOrder || !newValues) return false;

  // Compare against the order's current price (kept current by report handler + Phase 1)
  if (
    newValues.price !== undefined &&
    newValues.price !== originalOrder.price
  ) {
    return true;
  }

  if (
    newValues.quantity !== undefined &&
    newValues.quantity !== originalOrder.quantity
  ) {
    return true;
  }

  // Check OTO changes
  if (newValues.oto) {
    const originalOto = originalOrder.oto || [];

    if (newValues.oto.stopPrice !== undefined) {
      const originalStop = originalOto.find(
        (leg) =>
          leg.type === "stop" || (leg.orderType === "STOPLIMIT" && !leg.type),
      );
      if (newValues.oto.stopPrice !== (originalStop?.price ?? 0)) {
        return true;
      }
    }

    if (newValues.oto.profitPrice !== undefined) {
      const originalProfit = originalOto.find(
        (leg) =>
          leg.type === "profit" || (leg.orderType === "LIMIT" && !leg.type),
      );
      if (newValues.oto.profitPrice !== (originalProfit?.price ?? 0)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Compares original and new OTO price to detect changes
 * Used for individual OTO drag operations
 *
 * After the Phase 1 refactor, the OTO leg's price field is always current,
 * so we compare directly against it.
 *
 * @param {Object} originalOtoOrder - Original OTO order (from orderStore, always up-to-date)
 * @param {number} newPrice - New OTO price from drag operation
 * @returns {boolean} true if price actually changed, false otherwise
 */
export const hasOTOPriceChanged = (originalOtoOrder, newPrice) => {
  if (!originalOtoOrder || newPrice === undefined) return false;

  // Compare with small tolerance for floating point precision
  const priceDifference = Math.abs(originalOtoOrder.price - newPrice);
  const tolerance = 0.001;

  return priceDifference > tolerance;
};
